import React from 'react';
import { Pagination, Skeleton } from '@mui/material';
import ProcedureRow from './ProcedureRow';
import ProcedureEmptyState from './ProcedureEmptyState';
import styles from './ProcedureTable.module.css';

const SKELETON_ROWS = 4;

const paginationSx = {
    '& .MuiPaginationItem-root': { color: '#2D3748', borderRadius: '10px' },
    '& .Mui-selected': { backgroundColor: '#5A80FB !important', color: '#FFFFFF' },
    '& .MuiPaginationItem-root:hover': { backgroundColor: 'rgba(90, 128, 251, 0.1)' }
};

function SkeletonRows() {
    return Array.from({ length: SKELETON_ROWS }).map((_, index) => (
        <tr key={index}>
            <td>
                <div className={styles.identity}>
                    <Skeleton variant="rounded" width={38} height={38} sx={{ borderRadius: '10px', flexShrink: 0 }} />
                    <span className={styles.identityText}>
                        <Skeleton width="55%" height={18} />
                        <Skeleton width="80%" height={14} />
                    </span>
                </div>
            </td>
            <td><Skeleton width={84} height={26} sx={{ borderRadius: '999px' }} /></td>
            <td><Skeleton width={60} height={18} /></td>
            <td><Skeleton width={92} height={26} sx={{ borderRadius: '999px' }} /></td>
            <td><Skeleton width={72} height={32} sx={{ marginLeft: 'auto' }} /></td>
        </tr>
    ));
}

/**
 * Lista paginada dos procedimentos (ativos e inativos), com carregamento,
 * estado vazio e rodapé de paginação. As ações sobem para a página.
 */
function ProcedureTable({
    loading, procedures, items, currentPage, totalPages, activeCount,
    canManage, addBlockedReason, reactivateLimitMessage,
    onPageChange, onAdd, onEdit, onInactivate, onReactivate
}) {
    const inactiveCount = procedures.length - activeCount;
    const isEmpty = !loading && procedures.length === 0;
    const countText = `${activeCount} ${activeCount === 1 ? 'ativo' : 'ativos'}`
        + (inactiveCount > 0 ? `, ${inactiveCount} ${inactiveCount === 1 ? 'inativo' : 'inativos'}` : '');

    return (
        <section className={styles.card}>
            <header className={styles.header}>
                <div>
                    <h3 className={styles.title}>Seus procedimentos</h3>
                    <span className={styles.subtitle}>Valor e duração entram em cada agendamento.</span>
                </div>
            </header>

            {isEmpty ? (
                <ProcedureEmptyState canManage={canManage} blockedReason={addBlockedReason} onAdd={onAdd} />
            ) : (
                <div className={styles.tableWrapper}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Procedimento</th>
                                <th>Valor</th>
                                <th>Duração</th>
                                <th>Ag. público</th>
                                <th className={styles.actionsHead}>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? <SkeletonRows /> : items.map((procedure) => (
                                <ProcedureRow
                                    key={procedure.Id}
                                    procedure={procedure}
                                    canManage={canManage}
                                    reactivateLimitMessage={reactivateLimitMessage}
                                    onEdit={onEdit}
                                    onInactivate={onInactivate}
                                    onReactivate={onReactivate}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {!loading && procedures.length > 0 && (
                <footer className={styles.footer}>
                    <span className={styles.footerText}>
                        Página {currentPage} de {totalPages || 1} ({countText})
                    </span>
                    {totalPages > 1 && (
                        <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={onPageChange}
                            color="primary"
                            shape="rounded"
                            size="small"
                            sx={paginationSx}
                        />
                    )}
                </footer>
            )}
        </section>
    );
}

export default ProcedureTable;
